import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { chatResponseAsync } from "features/actions/chat";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import moment from "moment";
import chatImage from "assets/images/chat.jpg";
import defaultAvatar from "assets/images/dummy.png";
import { clearChatResponse } from "features/reducers/chatbot";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const Chatbot = ({ isOpen, messages, setMessages, setIsExpandedChatWindow }) => {
  const dispatch = useDispatch();
  const { chatResponse, chatLoading } = useSelector((state) => state.chat);
  const { getprofile } = useSelector((state) => state.auth);
  
  const commonId = localStorage.getItem("common_id");

  const [input, setInput] = useState("");
  const [chatState, setChatState] = useState(false);
  const [currentChat, setCurrentChat] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null); // ✅ Input reference for focusing

  const hasWelcomedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasWelcomedRef.current && messages.length === 0) {
      hasWelcomedRef.current = true;
      const welcomeMessage = {
        from: "bot",
        text: "Hello, welcome to your AI Assistant for the TavloAI Platform. Please ask anything related to your business and reviews.",
        time: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length, setMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (chatResponse?.Answer !== undefined) {
      const botMessage = {
        from: "bot",
        text: chatResponse.Answer || "We couldn't find any response.",
        time: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);

      // Clear the chatResponse so it doesn't append again on re-open
      dispatch(clearChatResponse());

      // ✅ Focus input after response
      inputRef.current?.focus();
    }
  }, [chatResponse, dispatch]);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  
    return () => {
      document.body.style.overflow = "auto"; // reset on unmount
    };
  }, [isExpanded]);
  

  const handleSend = () => {
    if (!input.trim() || chatLoading) return;

    const userMessage = {
      from: "user",
      text: input,
      time: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setCurrentChat(input);
    setChatState(true);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !chatLoading) handleSend();
  };

  useEffect(() => {
    if (chatState && currentChat) {
      dispatch(
        chatResponseAsync({
          common_id: commonId,
          query: currentChat,
        })
      ).finally(() => {
        setChatState(false);
        setCurrentChat("");
      });
    }
  }, [chatState]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: isExpanded ? "80vh" : "100%",
        width: isExpanded ? "80vw" : "100%",
        position: isExpanded ? "fixed" : "relative",
        top: isExpanded ? "20vh": 0,
        left: isExpanded ? "20vw" : 0,
        zIndex: 1000,
        backgroundColor: "#fff",
        fontFamily: "Arial, sans-serif",
        boxShadow: isExpanded ? "0 0 20px rgba(0,0,0,0.3)" : "none",
        transition: "all 0.3s ease-in-out",
        overflow: "hidden", // ✅ Prevent body scroll leaking
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "#1976d2",
          color: "white",
          padding: isExpanded ? "15px 40px" : "10px 15px",
          fontWeight: "bold",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>AI Assistant</span>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Clear Chat Icon */}
          <button
            onClick={() => {
              setMessages([]);
              dispatch(clearChatResponse());
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "white",
            }}
            title="Clear Chat"
          >
            <DeleteOutlineIcon />
          </button>

          {/* Expand/Collapse Icon */}
          <button
            onClick={() => {setIsExpanded((prev) => !prev); setIsExpandedChatWindow((prev) => !prev)}}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "white",
            }}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div
        style={{
          flex: 1,
          padding: "10px",
          overflowY: "auto",
          backgroundColor: "#f5f5f5",
          maxHeight: isExpanded ? "calc(100vh - 130px)" : "unset", // ✅ Adjust based on header + input area height
        }}
      >
        {messages.map((msg, index) => {
          const isUser = msg.from === "user";
          const avatar =
            isUser && getprofile?.userProfile?.profile_picture
              ? getprofile.userProfile.profile_picture
              : !isUser
              ? chatImage
              : defaultAvatar;

          return (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start",
                marginBottom: "12px",
                alignItems: "flex-end",
              }}
            >
              {!isUser && (
                <img
                  src={avatar}
                  alt="avatar"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    marginRight: 8,
                  }}
                />
              )}
              <div
                style={{
                  maxWidth: "70%",
                  padding: "3px 4px 10px 20px",
                  borderRadius: "15px",
                  backgroundColor: isUser ? "#fff" : "#318BEC10",
                  color: isUser ? "#585858" : "black",
                  fontSize: "14px",
                  border: isUser ? "1.7px solid #E2E8F0" : "1px solid #318BEC",
                  fontWeight: isUser ? "600" : ""
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.text}
                </ReactMarkdown>
                <div
                  style={{
                    fontSize: "11px",
                    marginTop: 4,
                    textAlign: isUser ? "right" : "left",
                    color: "#0a0a0a",
                    fontWeight: "400"
                  }}
                >
                  {moment(msg.time).fromNow()}
                </div>
              </div>
              {isUser && (
                <img
                  src={avatar}
                  alt="avatar"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    marginLeft: 8,
                  }}
                />
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        style={{
          display: "flex",
          borderTop: "1px solid #ddd",
          padding: "10px",
          backgroundColor: "#fff",
        }}
      >
        <input
          ref={inputRef} // ✅ Input gets auto-focused
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={chatLoading ? "Thinking..." : "Type your message..."}
          disabled={chatLoading}
          style={{
            flex: 1,
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            marginRight: "10px",
            fontSize: "14px",
            backgroundColor: chatLoading ? "#f0f0f0" : "#fff",
          }}
        />
        <button
          onClick={handleSend}
          disabled={chatLoading}
          style={{
            backgroundColor: chatLoading ? "#ccc" : "#1976d2",
            color: "white",
            border: "none",
            padding: "10px 15px",
            borderRadius: "5px",
            cursor: chatLoading ? "not-allowed" : "pointer",
            fontWeight: "bold",
          }}
        >
          {chatLoading ? "..." : "Send"}
        </button>
      </div>
    </div>
  );
};

export default Chatbot;
